import { AxiosResponse } from 'axios';
import { AnyObject } from 'common/interfaces';
import { getSupportBoxContacts } from 'components/hilfecenter/hilfecenter-service';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getResponseFormData } from 'utils/get-response-data';
import cloneDeep from 'lodash/cloneDeep';
import useAvatarById from '../use-avatar-by-id';

const normalizeSupportBoxPath = (pageUrl: string) => pageUrl.replace(/^\/+/, '').replace(/\/$/, '');

const toAttributeValue = (value: unknown) => ({ attributeValue: value ?? null });

const normalizeSupportContact = (contact: AnyObject) => {
  if (contact.contactid?.attributeValue) {
    return contact;
  }

  const attributes = contact.attributes || {};

  return {
    ...contact,
    firstname: toAttributeValue(attributes.firstname),
    lastname: toAttributeValue(attributes.lastname),
    contactid: toAttributeValue(attributes.contactid || contact.id),
    jobtitle: toAttributeValue(attributes.jobtitle),
    department: toAttributeValue(attributes.department),
    emailaddress1: toAttributeValue(attributes.emailaddress1)
  };
};

const normalizeSupportBox = (supportBox: AnyObject) => ({
  ...supportBox,
  contacts: Array.isArray(supportBox?.contacts) ? supportBox.contacts.map(normalizeSupportContact) : supportBox?.contacts
});

const converResponse = (response: AxiosResponse | undefined, pageUrl: string) => {
  if (response) {
    const responseData = response.data?.data;
    const normalizedPageUrl = normalizeSupportBoxPath(pageUrl);

    if (Array.isArray(responseData?.contacts)) {
      return normalizeSupportBox(responseData);
    }

    if (Array.isArray(responseData)) {
      return normalizeSupportBox({ contacts: responseData });
    }

    if (!Array.isArray(responseData?.items)) {
      return responseData?.contacts ? normalizeSupportBox(responseData) : responseData;
    }

    const updatedResponse = getResponseFormData(response.data, [{ name: 'items', type: 'array' }], true) as AnyObject[];
    const contact = updatedResponse
      .filter((item: AnyObject) => normalizeSupportBoxPath(item.uds_page_url || '') === normalizedPageUrl)
      .reduce((acc: AnyObject, curr: AnyObject) => {
        return {
          ...acc,
          ...curr
        };
      }, {});

    const supportBox = Object.keys(contact).length ? contact : updatedResponse.length === 1 ? updatedResponse[0] : undefined;

    return supportBox?.contacts ? normalizeSupportBox(supportBox) : supportBox;
  }
};

export const useSupportBoxQuery = (pageUrl: string, enabled: boolean) => {
  const [supportConttacts, setSupportContacts] = useState<AnyObject[]>([]);
  const supportBoxPath = normalizeSupportBoxPath(pageUrl);
  const { data, isLoading } = useQuery({
    queryKey: ['supportBoxContacts', supportBoxPath],
    queryFn: () => getSupportBoxContacts({ path: supportBoxPath }),
    select: data => converResponse(data, pageUrl),
    enabled
  });
  const [isAvatarsFetching, setIsAvatarsFetching] = useState(false);
  const { getAvatarById } = useAvatarById();

  const getAvatar = async (id: string) => {
    const avatar = await getAvatarById(id);
    if (avatar) {
      return {
        id,
        image: avatar
      };
    }
  };

  const getEmployeesAvatars = async () => {
    try {
      setIsAvatarsFetching(true);
      const employeesArr = cloneDeep(data?.contacts) as AnyObject[];
      const idArr = employeesArr.map((employee: AnyObject) => employee.contactid?.attributeValue);
      const promiseArr = idArr.map(async (id: string) => getAvatar(id));
      const response = await Promise.all(promiseArr);
      const updatedEmployees = employeesArr.map((employee: AnyObject) => {
        const image = response
          .filter(obj => obj)
          .find(
            (item: { id: string; image: string } | undefined) => item?.id === employee.contactid?.attributeValue
          )?.image;

        return {
          ...employee,
          image
        };
      });

      setSupportContacts(updatedEmployees);
    } catch (error) {
      console.log(error);
    } finally {
      setIsAvatarsFetching(false);
    }
  };

  useEffect(() => {
    if (data?.contacts?.length) {
      getEmployeesAvatars();
    }
  }, [data?.contacts?.length]);

  return {
    supportBox: data?.contacts?.length ? { ...data, contacts: supportConttacts } : data,
    isLoading: isLoading || isAvatarsFetching
  };
};
